import React, { useState, useRef, useEffect } from "react";
import {
  TextField, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox,
  RadioGroup, Radio, FormLabel, FormGroup, FormHelperText, Button, Box, Typography, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Popover, Autocomplete,
  IconButton, LinearProgress, Stack
} from "@mui/material";
import { CloudUpload, Delete, Visibility, InsertDriveFile, CheckCircle, Error as ErrorIcon, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { formatFileSize, validateFile, getAcceptAttribute, isImageFile, isPDFFile } from "../utils/fileUtils";
import dayjs from "dayjs";

// Custom date picker with month + year dropdowns (no extra dependency).
// Stores/returns the value as a 'YYYY-MM-DD' string, same as a native date input.
// Supports optional constraints via props.validation:
//   min_date (YYYY-MM-DD) — earliest selectable date
//   max_date (YYYY-MM-DD) — latest selectable date
//   min_age  (integer)    — minimum age in years (computed as max_date = today minus min_age years)
function MonthYearDatePicker(props) {
  var E = React.createElement;
  var value = props.value || "";
  var st = useState(null); var anchor = st[0], setAnchor = st[1];
  var initial = value ? dayjs(value) : dayjs();
  var ys = useState(initial.year()); var year = ys[0], setYear = ys[1];
  var ms = useState(initial.month()); var month = ms[0], setMonth = ms[1];

  // Resolve constraints from props.validation (all optional)
  var v = props.validation || {};
  var minDate = v.min_date ? dayjs(v.min_date) : null;
  var maxDate = v.max_date ? dayjs(v.max_date) : null;
  var minAge = v.min_age ? parseInt(v.min_age) : 0;
  // min_age overrides max_date: person must be at least N years old today
  if (minAge > 0) {
    var ageLimit = dayjs().subtract(minAge, "year");
    if (!maxDate || ageLimit.isBefore(maxDate)) maxDate = ageLimit;
  }

  function open(e) {
    var d = value ? dayjs(value) : dayjs();
    setYear(d.year()); setMonth(d.month()); setAnchor(e.currentTarget);
  }
  function close() { setAnchor(null); }
  function pick(day) { props.onChange(dayjs(new Date(year, month, day)).format("YYYY-MM-DD")); close(); }

  // Check if a specific day is disabled by constraints
  function isDayDisabled(dnum) {
    var d = dayjs(new Date(year, month, dnum));
    if (minDate && d.isBefore(minDate, "day")) return true;
    if (maxDate && d.isAfter(maxDate, "day")) return true;
    return false;
  }

  var monthsList = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  var weekdays = ["Su","Mo","Tu","We","Th","Fr","Sa"];
  var start = dayjs(new Date(year, month, 1));
  var daysInMonth = start.daysInMonth();
  var firstWeekday = start.day();
  var nowY = dayjs().year();
  var years = []; for (var y = nowY + 10; y >= 1940; y--) { years.push(y); }
  var sel = value ? dayjs(value) : null;

  var cells = [];
  var i;
  for (i = 0; i < firstWeekday; i++) { cells.push(E(Box, {key: "e" + i})); }
  for (i = 1; i <= daysInMonth; i++) {
    (function (dnum) {
      var isSel = sel && sel.date() === dnum && sel.month() === month && sel.year() === year;
      var disabled = isDayDisabled(dnum);
      cells.push(E(Box, {key: "d" + dnum, sx: {display: "flex", justifyContent: "center"}},
        E(Button, {size: "small", onClick: disabled ? undefined : function () { pick(dnum); },
          variant: isSel ? "contained" : "text",
          disabled: disabled,
          sx: {minWidth: 0, width: 32, height: 32, p: 0, borderRadius: "50%",
            opacity: disabled ? 0.3 : 1,
            color: disabled ? "text.disabled" : undefined,
            textDecoration: disabled ? "line-through" : "none",
            cursor: disabled ? "not-allowed" : "pointer"
          }}, String(dnum))));
    })(i);
  }

  // Build constraint warning text
  var warnings = [];
  if (minDate && !minAge) warnings.push("From " + minDate.format("DD MMM YYYY"));
  if (maxDate && !minAge) warnings.push("Until " + maxDate.format("DD MMM YYYY"));
  if (minAge > 0) warnings.push("Must be at least " + minAge + " years old (born on or before " + maxDate.format("DD MMM YYYY") + ")");
  var warningText = warnings.join(" • ");

  return E(Box, null,
    E(TextField, {fullWidth: true, size: "small", value: value, placeholder: "Select a date",
      onClick: props.readOnly ? undefined : open, disabled: props.readOnly,
      InputProps: {readOnly: true}, inputProps: {style: {cursor: props.readOnly ? "default" : "pointer"}}}),
    warningText ? E(Typography, {variant: "caption", sx: {mt: 0.5, display: "block", color: "text.secondary"}}, warningText) : null,
    E(Popover, {open: !!anchor, anchorEl: anchor, onClose: close,
      anchorOrigin: {vertical: "bottom", horizontal: "left"}},
      E(Box, {sx: {p: 2, width: 300}},
        warningText ? E(Typography, {variant: "caption", sx: {mb: 1, display: "block", color: "warning.main", fontWeight: 500}}, warningText) : null,
        E(Box, {sx: {display: "flex", gap: 0.5, mb: 1.5, alignItems: "center"}},
          E(IconButton, {size: "small", onClick: function() {
            if (month === 0) { setMonth(11); setYear(year - 1); } else { setMonth(month - 1); }
          }, sx: {p: 0.5}}, E(ChevronLeft, {fontSize: "small"})),
          E(Select, {size: "small", value: month, onChange: function (e) { setMonth(e.target.value); }, sx: {flex: 1}},
            monthsList.map(function (m, idx) { return E(MenuItem, {key: idx, value: idx}, m); })),
          E(Select, {size: "small", value: year, onChange: function (e) { setYear(e.target.value); }, sx: {width: 100}},
            years.map(function (yy) { return E(MenuItem, {key: yy, value: yy}, String(yy)); })),
          E(IconButton, {size: "small", onClick: function() {
            if (month === 11) { setMonth(0); setYear(year + 1); } else { setMonth(month + 1); }
          }, sx: {p: 0.5}}, E(ChevronRight, {fontSize: "small"}))),
        E(Box, {sx: {display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, textAlign: "center", mb: 0.5}},
          weekdays.map(function (w) { return E(Typography, {key: w, variant: "caption", color: "text.secondary"}, w); })),
        E(Box, {sx: {display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5}}, cells),
        E(Box, {sx: {display: "flex", justifyContent: "space-between", mt: 1}},
          E(Button, {size: "small", onClick: function () { props.onChange(""); close(); }}, "Clear"),
          E(Button, {size: "small", onClick: function () { var t = dayjs(); setYear(t.year()); setMonth(t.month()); }}, "Today")))));
}

// Fallback list, used only if /api/public/countries cannot be reached.
// The live list lives in the `countries` table and is edited from
// Admin -> Countries. See migration 010_countries.sql.
var FALLBACK_COUNTRIES = [
  {name:"Afghanistan", iso2:"AF", dial_code:"+93"},
  {name:"Australia", iso2:"AU", dial_code:"+61"},
  {name:"Bangladesh", iso2:"BD", dial_code:"+880"},
  {name:"Brazil", iso2:"BR", dial_code:"+55"},
  {name:"Canada", iso2:"CA", dial_code:"+1"},
  {name:"China", iso2:"CN", dial_code:"+86"},
  {name:"France", iso2:"FR", dial_code:"+33"},
  {name:"Germany", iso2:"DE", dial_code:"+49"},
  {name:"India", iso2:"IN", dial_code:"+91"},
  {name:"Indonesia", iso2:"ID", dial_code:"+62"},
  {name:"Kenya", iso2:"KE", dial_code:"+254"},
  {name:"Nepal", iso2:"NP", dial_code:"+977"},
  {name:"Nigeria", iso2:"NG", dial_code:"+234"},
  {name:"Pakistan", iso2:"PK", dial_code:"+92"},
  {name:"Philippines", iso2:"PH", dial_code:"+63"},
  {name:"South Africa", iso2:"ZA", dial_code:"+27"},
  {name:"Sri Lanka", iso2:"LK", dial_code:"+94"},
  {name:"United Kingdom", iso2:"GB", dial_code:"+44"},
  {name:"United States", iso2:"US", dial_code:"+1"}
];

// Fetched once per page load and shared by every country and phone field.
var countryPromise = null;
function loadCountries() {
  if (countryPromise) return countryPromise;
  countryPromise = fetch("/api/public/countries")
    .then(function(r) { return r.json(); })
    .then(function(j) {
      var list = (j && j.data) || [];
      return list.length ? list : FALLBACK_COUNTRIES;
    })
    .catch(function() { return FALLBACK_COUNTRIES; });
  return countryPromise;
}

function useCountries() {
  var st = useState(null); var list = st[0], setList = st[1];
  useEffect(function() {
    var alive = true;
    loadCountries().then(function(l) { if (alive) setList(l); });
    return function() { alive = false; };
  }, []);
  return list;
}

function extractYouTubeId(url) {
  if (!url) return null;
  var m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|.*[?&]v=))([^&#?]+)/);
  return m ? m[1] : null;
}

function countWords(text) {
  if (!text || typeof text !== 'string') return 0;
  return text.trim().split(/\s+/).filter(function(w) { return w.length > 0; }).length;
}

function DescriptionText(props) {
  if (!props.text) return null;
  var E = React.createElement;
  if (/<[a-z][\s\S]*>/i.test(props.text)) {
    return E(Typography, {variant:"body2", color:"text.secondary", sx: Object.assign({mb:1}, props.sx || {}),
      dangerouslySetInnerHTML: {__html: props.text}});
  }
  return E(Typography, {variant:"body2", color:"text.secondary",
    sx: Object.assign({mb:1, whiteSpace:"pre-wrap"}, props.sx || {})}, props.text);
}

function FieldWrapper(props) {
  var E = React.createElement;
  var field = props.field;
  return E(Box, {sx:{p:2, border:'1px solid', borderColor:'divider', borderRadius:1, bgcolor:'white'}},
    E(Typography, {variant:"subtitle1", sx:{fontWeight:600, mb:0.5, color:'text.primary'}},
      field.label, field.required ? E("span", {style:{color:'#c0392b',marginLeft:4}}, "*") : null),
    field.help_text ? E(DescriptionText, {text: field.help_text, sx:{mb:1.5, fontSize:'0.85rem'}}) : null,
    props.children,
    props.error ? E(FormHelperText, {error:true}, props.error) : null);
}

function TermsDialog(props) {
  var contentRef = useRef(null);
  var st = useState(!props.mustScroll); var scrolled = st[0]; var setScrolled = st[1];
  function onScroll() {
    if (!contentRef.current || !props.mustScroll) return;
    var el = contentRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setScrolled(true);
  }
  useEffect(function() { if (props.open) setScrolled(!props.mustScroll); }, [props.open, props.mustScroll]);
  var E = React.createElement;
  return E(Dialog, {open:props.open, onClose:props.onClose, maxWidth:"md", fullWidth:true},
    E(DialogTitle, null, props.title || "Terms & Conditions"),
    E(DialogContent, {dividers:true, ref:contentRef, onScroll:onScroll, sx:{maxHeight:400,overflowY:"auto"}},
      props.url && !props.text
        ? E("iframe", {src:props.url, title:props.title, style:{width:"100%",height:360,border:"none"}})
        : E("div", {dangerouslySetInnerHTML:{__html: props.text || "No policy text."}, style:{fontSize:14}})),
    E(DialogActions, null,
      E(Button, {onClick:props.onClose}, "Close"),
      E(Button, {variant:"contained", onClick:props.onAccept, disabled:!scrolled}, "I have read and agree")));
}

function TermsField(props) {
  var ds = useState(false); var open = ds[0]; var setOpen = ds[1];
  var field = props.field; var v = field.validation || {};
  var requireOpen = v.require_open !== false;
  var E = React.createElement;
  return E(Box, null,
    field.help_text ? E(DescriptionText, {text: field.help_text, sx:{mb:1, fontSize:"0.85rem"}}) : null,
    E(FormControlLabel, {
      control: E(Checkbox, {
        checked:!!props.value,
        disabled: props.readOnly || (requireOpen && !props.value),
        onChange:function(e){
          if (requireOpen) { if(!e.target.checked) props.onChange(field.id, false); }
          else { props.onChange(field.id, e.target.checked); }
        }}),
      label: E(Typography, {variant:"body2"},
        "I agree to the ",
        E("span", {style:{color:"#1a5276",cursor:"pointer",textDecoration:"underline"},
          onClick:function(e){e.preventDefault();setOpen(true);}}, v.policy_title || field.label),
        field.required ? " *" : "")}),
    props.error ? E(FormHelperText, {error:true,sx:{ml:4}}, props.error) : null,
    E(TermsDialog, {open:open, onClose:function(){setOpen(false);},
      onAccept:function(){props.onChange(field.id, true); setOpen(false);},
      title:v.policy_title||field.label, text:v.policy_text, url:v.policy_url,
      mustScroll: requireOpen && (v.must_scroll!==false)}));
}

function PhoneField(props) {
  var field = props.field; var value = props.value || ""; var E = React.createElement;
  var countries = useCountries();
  var parts = value.split("-");
  var countryCode = parts.length > 1 ? parts[0] : "+91";
  var phoneNum = parts.length > 1 ? parts[1] : value;

  // One entry per distinct dialling code, labelled with the countries sharing it.
  var codes = [];
  var seen = {};
  (countries || FALLBACK_COUNTRIES).forEach(function(c) {
    if (!c.dial_code) return;
    if (seen[c.dial_code] !== undefined) {
      var at = seen[c.dial_code];
      if (codes[at].names.length < 3) codes[at].names.push(c.iso2 || c.name);
      return;
    }
    seen[c.dial_code] = codes.length;
    codes.push({code: c.dial_code, names: [c.iso2 || c.name]});
  });
  codes.sort(function(a, b) { return a.names[0].localeCompare(b.names[0]); });
  // The stored code may no longer be in the list; keep it selectable regardless.
  if (countryCode && seen[countryCode] === undefined) codes.unshift({code: countryCode, names: [countryCode]});

  return E(Box, {sx:{display:"flex", gap:1}},
    E(FormControl, {sx:{minWidth:140}},
      E(InputLabel, {size:"small"}, "Code"),
      E(Select, {value:countryCode, label:"Code", size:"small",
        MenuProps:{PaperProps:{style:{maxHeight:320}}},
        onChange:function(e){props.onChange(field.id, e.target.value + "-" + phoneNum);}, disabled:props.readOnly},
        codes.map(function(c) {
          return E(MenuItem, {key:c.code + "|" + c.names[0], value:c.code}, c.names.join("/") + " " + c.code);
        }))),
    E(TextField, {fullWidth:true, size:"small", placeholder:"9876543210",
      value:phoneNum, onChange:function(e){ props.onChange(field.id, countryCode + "-" + e.target.value.replace(/[^0-9]/g, "")); },
      disabled:props.readOnly, inputProps:{inputMode:"numeric", pattern:"[0-9]*"}}));
}

function TextareaWithWordCount(props) {
  var E = React.createElement;
  var wc = countWords(props.value);
  var minW = (props.validation && props.validation.min_words) || 0;
  var maxW = (props.validation && props.validation.max_words) || 0;
  var over = maxW > 0 && wc > maxW;
  var under = minW > 0 && wc < minW && wc > 0;
  return E(Box, null,
    E(TextField, {fullWidth:true, multiline:true, rows:4, placeholder:props.placeholder,
      value:props.value, onChange:props.onChange, disabled:props.readOnly, error:over||under}),
    E(Box, {sx:{display:'flex', justifyContent:'space-between', mt:0.5}},
      (minW > 0 || maxW > 0) ? E(Typography, {variant:"caption", color:over||under?"error":"text.secondary"},
        (minW > 0 ? "Min " + minW + " words" : ""), (minW > 0 && maxW > 0 ? " \u2022 " : ""),
        (maxW > 0 ? "Max " + maxW + " words" : "")) : E("span"),
      E(Chip, {label:wc + " words", size:"small", variant:"outlined",
        color:over?"error":under?"warning":"default", sx:{height:20, fontSize:11}})));
}

function UrlField(props) {
  var E = React.createElement;
  var field = props.field; var value = props.value || '';
  var us = useState(false); var invalid = us[0]; var setInvalid = us[1];
  function validate(v) {
    if (!v) { setInvalid(false); return; }
    try { new URL(v); setInvalid(false); } catch(e6) { setInvalid(true); }
  }
  return E(FieldWrapper, {field:field, error:props.error || (invalid ? 'Please enter a valid URL (e.g. https://example.com)' : null)},
    E(TextField, {fullWidth:true, size:"small", placeholder:field.placeholder || "https://",
      value:value, disabled:props.readOnly,
      onChange:function(e) { props.onChange(field.id, e.target.value); validate(e.target.value); },
      onBlur:function() { validate(value); },
      error:invalid}));
}

function VideoSelector(props) {
  var E = React.createElement;
  var field = props.field; var value = props.value || '';
  var v = field.validation || {};
  var urls = (v.video_urls || []).filter(function(u) { return u && u.trim(); });
  var singleUrl = v.video_url || '';

  // Single video mode
  if (urls.length === 0 && singleUrl) {
    var vid = extractYouTubeId(singleUrl);
    return E(FieldWrapper, {field:field, error:props.error}, E(Box, null,
      vid ? E(Box, {sx:{position:"relative",paddingBottom:"56.25%",height:0,mb:2,borderRadius:2,overflow:"hidden"}},
        E("iframe",{src:"https://www.youtube-nocookie.com/embed/"+vid, title:field.label, style:{position:"absolute",top:0,left:0,width:"100%",height:"100%",border:0}, allow:"accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture", allowFullScreen:true})) : null,
      v.has_response ? E(TextField,{fullWidth:true,size:"small",placeholder:"Your response",multiline:true,rows:3,value:value,onChange:function(e){props.onChange(field.id,e.target.value);},disabled:props.readOnly}) : null));
  }

  // Multi-video mode: user selects one
  if (urls.length > 0) {
    // Parse value: could be "VIDEO_INDEX|response_text"
    var parts = (value || '').split('|');
    var selectedIdx = parts[0] ? parseInt(parts[0]) : -1;
    var responseText = parts.length > 1 ? parts.slice(1).join('|') : '';
    var selectedUrl = (selectedIdx >= 0 && selectedIdx < urls.length) ? urls[selectedIdx] : '';
    var selectedVid = extractYouTubeId(selectedUrl);

    return E(FieldWrapper, {field:field, error:props.error}, E(Box, null,
      E(Typography, {variant:"body2", color:"text.secondary", sx:{mb:1}}, 'Select a video to respond to:'),
      E(FormControl, {fullWidth:true, size:"small", sx:{mb:2}},
        E(Select, {value: selectedIdx >= 0 ? selectedIdx : '', displayEmpty:true,
          onChange:function(e) {
            var idx = e.target.value;
            props.onChange(field.id, idx + '|' + responseText);
          }},
          E(MenuItem, {value:'', disabled:true}, E("em", null, "Choose a video...")),
          urls.map(function(u, i) {
            var title = v.video_titles && v.video_titles[i] ? v.video_titles[i] : 'Video ' + (i + 1);
            return E(MenuItem, {key:i, value:i}, title);
          }))),
      selectedVid ? E(Box, {sx:{position:"relative",paddingBottom:"56.25%",height:0,mb:2,borderRadius:2,overflow:"hidden"}},
        E("iframe",{src:"https://www.youtube-nocookie.com/embed/"+selectedVid, title:field.label, style:{position:"absolute",top:0,left:0,width:"100%",height:"100%",border:0}, allow:"accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture", allowFullScreen:true})) : null,
      v.has_response ? E(TextField,{fullWidth:true,size:"small",placeholder:"Your response",multiline:true,rows:3,
        value:responseText, onChange:function(e) { props.onChange(field.id, selectedIdx + '|' + e.target.value); },
        disabled:props.readOnly}) : null));
  }

  // No videos configured
  return E(FieldWrapper, {field:field, error:props.error},
    E(Typography, {variant:"body2", color:"text.secondary"}, "No video configured for this field."));
}

function CountryField(props) {
  var field = props.field; var E = React.createElement;
  var countries = useCountries();
  var names = (countries || FALLBACK_COUNTRIES).map(function(c) { return c.name; });
  var value = props.value || "";
  // A country recorded before it was removed from the list still has to display.
  if (value && names.indexOf(value) === -1) names = [value].concat(names);
  return E(Autocomplete, {
    options: names,
    value: value || null,
    disabled: props.readOnly,
    loading: countries === null,
    autoHighlight: true,
    onChange: function(_, v) { props.onChange(field.id, v || ""); },
    isOptionEqualToValue: function(o, v) { return o === v; },
    renderInput: function(p) {
      return E(TextField, Object.assign({}, p, {
        size: "small",
        placeholder: field.placeholder || "Start typing a country",
      }));
    }
  });
}

// StateField auto-detects the country field in the same form and loads states for it.
function StateField(props) {
  var field = props.field; var E = React.createElement;
  var value = props.value || "";
  var allFields = props.allFields || [];
  var values = props.values || {};
  var countries = useCountries();
  var ss = useState([]); var statesList = ss[0]; var setStatesList = ss[1];
  var ls = useState(false); var loading = ls[0]; var setLoading = ls[1];

  // Find the country field in the same form by type
  var countryField = null;
  for (var ci = 0; ci < allFields.length; ci++) {
    if (allFields[ci].type === "country") { countryField = allFields[ci]; break; }
  }
  var selectedCountry = countryField ? (values[countryField.id] || "") : "";

  // Map country name to ISO2
  var countryISO2 = "";
  if (selectedCountry && countries) {
    for (var ki = 0; ki < countries.length; ki++) {
      if (countries[ki].name === selectedCountry) { countryISO2 = countries[ki].iso2; break; }
    }
  }

  // Fetch states when country changes
  useEffect(function() {
    if (!countryISO2) { setStatesList([]); return; }
    var alive = true;
    setLoading(true);
    fetch("/api/public/states?country=" + countryISO2)
      .then(function(r) { return r.json(); })
      .then(function(j) {
        if (alive) {
          var list = (j && j.data) || [];
          setStatesList(list.map(function(s) { return s.name; }));
          setLoading(false);
        }
      })
      .catch(function() { if (alive) { setStatesList([]); setLoading(false); } });
    return function() { alive = false; };
  }, [countryISO2]);

  // Clear state value if country changes and current value is not in new list
  useEffect(function() {
    if (value && statesList.length > 0 && statesList.indexOf(value) === -1) {
      props.onChange(field.id, "");
    }
  }, [statesList]);

  if (!countryField) {
    return E(Typography, {variant: "body2", color: "text.secondary"},
      "Add a Country field to this form to enable state selection.");
  }
  if (!selectedCountry) {
    return E(TextField, {fullWidth: true, size: "small", disabled: true,
      placeholder: "Select a country first"});
  }

  var options = statesList.slice();
  if (value && options.indexOf(value) === -1) options = [value].concat(options);

  return E(Autocomplete, {
    options: options,
    value: value || null,
    disabled: props.readOnly,
    loading: loading,
    autoHighlight: true,
    onChange: function(_, v) { props.onChange(field.id, v || ""); },
    isOptionEqualToValue: function(o, v) { return o === v; },
    renderInput: function(p) {
      return E(TextField, Object.assign({}, p, {
        size: "small",
        placeholder: field.placeholder || "Start typing a state/province",
      }));
    }
  });
}

// AirportField auto-detects the country field and loads airports for it.
function AirportField(props) {
  var field = props.field; var E = React.createElement;
  var value = props.value || "";
  var allFields = props.allFields || [];
  var values = props.values || {};
  var countries = useCountries();
  var as = useState([]); var airportsList = as[0]; var setAirportsList = as[1];
  var ls = useState(false); var loading = ls[0]; var setLoading = ls[1];

  // Find the country field in the same form by type
  var countryField = null;
  for (var ci = 0; ci < allFields.length; ci++) {
    if (allFields[ci].type === "country") { countryField = allFields[ci]; break; }
  }
  var selectedCountry = countryField ? (values[countryField.id] || "") : "";

  // Map country name to ISO2
  var countryISO2 = "";
  if (selectedCountry && countries) {
    for (var ki = 0; ki < countries.length; ki++) {
      if (countries[ki].name === selectedCountry) { countryISO2 = countries[ki].iso2; break; }
    }
  }

  // Fetch airports when country changes
  useEffect(function() {
    if (!countryISO2) { setAirportsList([]); return; }
    var alive = true;
    setLoading(true);
    fetch("/api/public/airports?country=" + countryISO2)
      .then(function(r) { return r.json(); })
      .then(function(j) {
        if (alive) {
          var list = (j && j.data) || [];
          setAirportsList(list);
          setLoading(false);
        }
      })
      .catch(function() { if (alive) { setAirportsList([]); setLoading(false); } });
    return function() { alive = false; };
  }, [countryISO2]);

  // Clear value if country changes and current value is not in new list
  useEffect(function() {
    if (value && airportsList.length > 0) {
      var found = false;
      for (var i = 0; i < airportsList.length; i++) {
        if (airportsList[i].iata_code === value || airportsList[i].name === value) { found = true; break; }
      }
      if (!found) props.onChange(field.id, "");
    }
  }, [airportsList]);

  if (!countryField) {
    return E(Typography, {variant: "body2", color: "text.secondary"},
      "Add a Country field to this form to enable airport selection.");
  }
  if (!selectedCountry) {
    return E(TextField, {fullWidth: true, size: "small", disabled: true,
      placeholder: "Select a country first"});
  }

  // Build display options: "City - Name (IATA)"
  var options = airportsList.map(function(a) {
    return { label: a.city + " - " + a.name + " (" + a.iata_code + ")", value: a.iata_code, name: a.name };
  });
  // Keep existing value if not in current list
  if (value && !options.some(function(o) { return o.value === value; })) {
    options = [{ label: value, value: value, name: value }].concat(options);
  }
  var selected = options.find(function(o) { return o.value === value; }) || null;

  return E(Autocomplete, {
    options: options,
    value: selected,
    disabled: props.readOnly,
    loading: loading,
    autoHighlight: true,
    getOptionLabel: function(o) { return o.label || ""; },
    isOptionEqualToValue: function(o, v) { return o.value === v.value; },
    onChange: function(_, v) { props.onChange(field.id, v ? v.value : ""); },
    renderInput: function(p) {
      return E(TextField, Object.assign({}, p, {
        size: "small",
        placeholder: field.placeholder || "Search for an airport",
      }));
    }
  });
}

// ComboField renders multiple sub-inputs under one question label.
// The value is stored as an object: { part_1: "...", part_2: "...", ... }
function ComboField(props) {
  var field = props.field; var E = React.createElement;
  var value = (typeof props.value === 'object' && props.value !== null) ? props.value : {};
  var subFields = field.sub_fields || [];

  function countWords(text) {
    if (!text || typeof text !== 'string') return 0;
    return text.trim().split(/\s+/).filter(function(w) { return w.length > 0; }).length;
  }

  function handleSubChange(key, val) {
    var next = Object.assign({}, value);
    next[key] = val;
    props.onChange(field.id, next);
  }

  function handleSubFileChange(key, file) {
    if (file && props.onFileChange) {
      // Store file under a composite key: field.id + "__" + subfield key
      var fileKey = field.id + "__" + key;
      props.onFileChange(fileKey, file);
      handleSubChange(key, file.name || "uploaded");
    } else if (!file && props.onFileChange) {
      var fileKey2 = field.id + "__" + key;
      props.onFileChange(fileKey2, null);
      handleSubChange(key, "");
    }
  }

  if (subFields.length === 0) {
    return E(Typography, {variant: "body2", color: "text.secondary"},
      "No sub-fields configured. Edit this field in the form builder to add parts.");
  }

  return E(Box, {sx: {display: "flex", flexDirection: "column", gap: 2}},
    subFields.map(function(sf, idx) {
      var sfKey = sf.key || ("part_" + (idx + 1));
      var sfVal = value[sfKey] || "";
      var sfLabel = sf.label || ("Part " + (idx + 1));
      var sfRequired = !!sf.required;
      var sfValidation = sf.validation || {};

      switch (sf.type) {
        case "text":
          return E(Box, {key: sfKey},
            E(Typography, {variant: "body2", fontWeight: 500, sx: {mb: 0.5}},
              sfLabel, sfRequired ? E("span", {style: {color: "#c0392b", marginLeft: 4}}, "*") : null),
            E(TextField, {fullWidth: true, size: "small", value: sfVal,
              placeholder: sf.placeholder || "",
              disabled: props.readOnly,
              onChange: function(e) { handleSubChange(sfKey, e.target.value); }}));

        case "textarea":
          var wc = countWords(sfVal);
          var maxW = sfValidation.max_words || 0;
          var minW = sfValidation.min_words || 0;
          var over = maxW > 0 && wc > maxW;
          var under = minW > 0 && wc < minW && wc > 0;
          return E(Box, {key: sfKey},
            E(Typography, {variant: "body2", fontWeight: 500, sx: {mb: 0.5}},
              sfLabel, sfRequired ? E("span", {style: {color: "#c0392b", marginLeft: 4}}, "*") : null),
            E(TextField, {fullWidth: true, multiline: true, rows: 3, value: sfVal,
              placeholder: sf.placeholder || "",
              disabled: props.readOnly,
              error: over || under,
              onChange: function(e) { handleSubChange(sfKey, e.target.value); }}),
            (maxW > 0 || minW > 0) ? E(Box, {sx: {display: "flex", justifyContent: "space-between", mt: 0.5}},
              E(Typography, {variant: "caption", color: over || under ? "error" : "text.secondary"},
                (minW > 0 ? "Min " + minW + " words" : ""),
                (minW > 0 && maxW > 0 ? " • " : ""),
                (maxW > 0 ? "Max " + maxW + " words" : "")),
              E(Typography, {variant: "caption", color: over ? "error" : under ? "warning.main" : "text.secondary"},
                wc + " words")) : null);

        case "url":
          return E(Box, {key: sfKey},
            E(Typography, {variant: "body2", fontWeight: 500, sx: {mb: 0.5}},
              sfLabel, sfRequired ? E("span", {style: {color: "#c0392b", marginLeft: 4}}, "*") : null),
            E(TextField, {fullWidth: true, size: "small", value: sfVal,
              placeholder: sf.placeholder || "https://",
              disabled: props.readOnly,
              onChange: function(e) { handleSubChange(sfKey, e.target.value); }}));

        case "file_upload":
          var inputRef = React.createRef();
          var hasFile = sfVal && sfVal !== "";
          return E(Box, {key: sfKey},
            E(Typography, {variant: "body2", fontWeight: 500, sx: {mb: 0.5}},
              sfLabel, sfRequired ? E("span", {style: {color: "#c0392b", marginLeft: 4}}, "*") : null),
            E(Box, {sx: {display: "flex", alignItems: "center", gap: 1}},
              E(Button, {variant: "outlined", size: "small", disabled: props.readOnly,
                onClick: function() { if (inputRef.current) inputRef.current.click(); }},
                hasFile ? "Change file" : "Choose file"),
              hasFile ? E(Typography, {variant: "caption", color: "text.secondary"}, sfVal) : null,
              hasFile ? E(Button, {size: "small", color: "error", onClick: function() { handleSubFileChange(sfKey, null); }}, "Remove") : null),
            E("input", {ref: inputRef, type: "file", hidden: true,
              onChange: function(e) {
                if (e.target.files && e.target.files[0]) { handleSubFileChange(sfKey, e.target.files[0]); }
              }}));

        case "number":
          return E(Box, {key: sfKey},
            E(Typography, {variant: "body2", fontWeight: 500, sx: {mb: 0.5}},
              sfLabel, sfRequired ? E("span", {style: {color: "#c0392b", marginLeft: 4}}, "*") : null),
            E(TextField, {fullWidth: true, size: "small", type: "number", value: sfVal,
              disabled: props.readOnly,
              onChange: function(e) { handleSubChange(sfKey, e.target.value); }}));

        default:
          return E(Box, {key: sfKey},
            E(Typography, {variant: "body2", fontWeight: 500, sx: {mb: 0.5}}, sfLabel),
            E(TextField, {fullWidth: true, size: "small", value: sfVal,
              disabled: props.readOnly,
              onChange: function(e) { handleSubChange(sfKey, e.target.value); }}));
      }
    }));
}

function FileUploadField(props) {
  var field = props.field;
  var value = props.value;
  var error = props.error;
  var E = React.createElement;
  var inputRef = useRef(null);
  var ds = useState(false); var previewOpen = ds[0]; var setPreviewOpen = ds[1];
  var vs = useState(null); var validationError = vs[0]; var setValidationError = vs[1];
  var ps = useState(null); var previewUrl = ps[0]; var setPreviewUrl = ps[1];

  var validation = field.validation || {};
  var maxMB = validation.max_file_size_mb || 50;
  var allowedExts = validation.allowed_extensions || [];
  var acceptAttr = getAcceptAttribute(validation);

  // Determine the display name and whether a file is selected
  var fileName = null;
  var fileObj = null;
  if (value && typeof value === 'object' && value.name) {
    fileName = value.name;
    fileObj = value;
  } else if (value && typeof value === 'string') {
    fileName = value;
  }
  var hasFile = !!fileName;

  // Build preview URL for the file object
  useEffect(function() {
    if (fileObj && (isImageFile(fileObj.name) || isPDFFile(fileObj.name))) {
      var url = URL.createObjectURL(fileObj);
      setPreviewUrl(url);
      return function() { URL.revokeObjectURL(url); };
    } else {
      setPreviewUrl(null);
    }
  }, [fileObj]);

  function handleFileSelect(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;

    // Validate file
    var err = validateFile(file, validation);
    if (err) {
      setValidationError(err);
      // Reset the input
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setValidationError(null);
    if (props.onFileChange) props.onFileChange(field.id, file);
  }

  function handleRemove() {
    setValidationError(null);
    setPreviewUrl(null);
    if (inputRef.current) inputRef.current.value = '';
    if (props.onFileChange) props.onFileChange(field.id, null);
  }

  var canPreview = hasFile && fileObj && (isImageFile(fileName) || isPDFFile(fileName));

  // Constraints info text
  var constraints = [];
  if (maxMB) constraints.push('Max ' + maxMB + ' MB');
  if (allowedExts.length > 0) constraints.push('Accepted: ' + allowedExts.map(function(e) { return '.' + e.replace(/^\./, ''); }).join(', '));
  var constraintText = constraints.join(' • ');

  return E(FieldWrapper, {field: field, error: error || validationError},
    // File info area
    hasFile
      ? E(Box, {sx: {border: '1px solid', borderColor: validationError ? 'error.main' : 'success.main', borderRadius: 1, p: 1.5, mb: 1, bgcolor: validationError ? 'error.50' : 'success.50'}},
          E(Stack, {direction: 'row', alignItems: 'center', spacing: 1},
            validationError
              ? E(ErrorIcon, {sx: {color: 'error.main', fontSize: 20}})
              : E(CheckCircle, {sx: {color: 'success.main', fontSize: 20}}),
            E(InsertDriveFile, {sx: {color: 'text.secondary', fontSize: 18}}),
            E(Box, {sx: {flex: 1, minWidth: 0}},
              E(Typography, {variant: 'body2', sx: {fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}, fileName),
              fileObj ? E(Typography, {variant: 'caption', color: 'text.secondary'}, formatFileSize(fileObj.size)) : null),
            canPreview ? E(IconButton, {size: 'small', onClick: function() { setPreviewOpen(true); }, title: 'Preview file'},
              E(Visibility, {fontSize: 'small'})) : null,
            !props.readOnly ? E(IconButton, {size: 'small', onClick: handleRemove, title: 'Remove file', sx: {color: 'error.main'}},
              E(Delete, {fontSize: 'small'})) : null))
      : null,

    // Upload button (shown when no file or for re-upload)
    !hasFile && !props.readOnly
      ? E(Box, null,
          E(Button, {variant: 'outlined', component: 'label', startIcon: E(CloudUpload), size: 'small'},
            'Choose File',
            E('input', {type: 'file', hidden: true, ref: inputRef, accept: acceptAttr, onChange: handleFileSelect})))
      : null,

    // Re-upload button when file exists
    hasFile && !props.readOnly
      ? E(Button, {variant: 'text', component: 'label', size: 'small', startIcon: E(CloudUpload), sx: {mt: 0.5}},
          'Replace File',
          E('input', {type: 'file', hidden: true, ref: inputRef, accept: acceptAttr, onChange: handleFileSelect}))
      : null,

    // Constraints info
    constraintText ? E(Typography, {variant: 'caption', color: 'text.secondary', sx: {mt: 0.5, display: 'block'}}, constraintText) : null,

    // Preview dialog
    E(Dialog, {open: previewOpen, onClose: function() { setPreviewOpen(false); }, maxWidth: 'md', fullWidth: true},
      E(DialogTitle, null, 'File Preview: ' + (fileName || '')),
      E(DialogContent, {sx: {textAlign: 'center', minHeight: 300}},
        previewUrl && isImageFile(fileName)
          ? E('img', {src: previewUrl, alt: fileName, style: {maxWidth: '100%', maxHeight: 500, objectFit: 'contain'}})
          : previewUrl && isPDFFile(fileName)
            ? E('iframe', {src: previewUrl, title: fileName, style: {width: '100%', height: 500, border: 'none'}})
            : E(Typography, {color: 'text.secondary'}, 'Preview not available for this file type')),
      E(DialogActions, null,
        E(Button, {onClick: function() { setPreviewOpen(false); }}, 'Close'))));
}

function RenderField(props) {
  var field = props.field;
  var value = (props.values && props.values[field.id]) || "";
  var error = props.errors && props.errors[field.id];
  var E = React.createElement;
  switch (field.type) {
    case "text": case "email": case "number":
      return E(FieldWrapper, {field:field, error:error},
        E(TextField, {fullWidth:true, size:"small", type:field.type==="number"?"number":"text",
          placeholder:field.placeholder, value:value,
          onChange:function(e){props.onChange(field.id,e.target.value);}, disabled:props.readOnly}));
    case "url":
      return E(UrlField, {field:field, value:value, onChange:props.onChange, error:error, readOnly:props.readOnly});
    case "phone":
      return E(FieldWrapper, {field:field, error:error},
        E(PhoneField, {field:field, value:value, onChange:props.onChange, readOnly:props.readOnly}));
    case "textarea":
      return E(FieldWrapper, {field:field, error:error},
        E(TextareaWithWordCount, {value:value, placeholder:field.placeholder,
          validation:field.validation, readOnly:props.readOnly,
          onChange:function(e){props.onChange(field.id,e.target.value);}}));
    case "date":
      return E(FieldWrapper, {field:field, error:error},
        E(MonthYearDatePicker, {value:value, readOnly:props.readOnly,
          validation:field.validation,
          onChange:function(v){props.onChange(field.id, v);}}));
    case "country":
      return E(FieldWrapper, {field:field, error:error},
        E(CountryField, {field:field, value:value, onChange:props.onChange, readOnly:props.readOnly}));
    case "state":
      return E(FieldWrapper, {field:field, error:error},
        E(StateField, {field:field, value:value, onChange:props.onChange, readOnly:props.readOnly,
          allFields:props.allFields||[], values:props.values||{}}));
    case "airport":
      return E(FieldWrapper, {field:field, error:error},
        E(AirportField, {field:field, value:value, onChange:props.onChange, readOnly:props.readOnly,
          allFields:props.allFields||[], values:props.values||{}}));
    case "combo":
      return E(FieldWrapper, {field:field, error:error},
        E(ComboField, {field:field, value:value, onChange:props.onChange,
          onFileChange:props.onFileChange, readOnly:props.readOnly}));
    case "dropdown":
      var opts = field.options || [];
      return E(FieldWrapper, {field:field, error:error},
        E(FormControl, {fullWidth:true, size:"small"},
          E(Select, {value:value, displayEmpty:true, onChange:function(e){props.onChange(field.id,e.target.value);}},
            E(MenuItem, {value:"", disabled:true}, E("em", null, "Select...")),
            opts.map(function(o){return E(MenuItem,{key:o,value:o},o);}))));
    case "radio":
      var radioAllowOther = field.validation && field.validation.allow_other;
      var radioIsOther = radioAllowOther && value && value.indexOf("Other: ") === 0;
      var radioOtherText = radioIsOther ? value.substring(7) : "";
      // If value doesn't match any option and isn't empty, treat as "Other"
      var radioOptions = field.options || [];
      var radioIsOtherSelected = radioIsOther || (radioAllowOther && value && radioOptions.indexOf(value) === -1 && value !== "");
      var radioCurrentValue = radioIsOtherSelected ? "__other__" : value;
      return E(FieldWrapper, {field:field, error:error},
        E(RadioGroup, {value:radioCurrentValue, onChange:function(e){
          if (e.target.value === "__other__") {
            props.onChange(field.id, "Other: ");
          } else {
            props.onChange(field.id, e.target.value);
          }
        }},
          radioOptions.map(function(o){return E(FormControlLabel,{key:o,value:o,control:E(Radio,{disabled:props.readOnly}),label:o});}),
          radioAllowOther ? E(FormControlLabel, {key:"__other__", value:"__other__",
            control:E(Radio, {disabled:props.readOnly}),
            label:E(Box, {sx:{display:"flex", alignItems:"center", gap:1}},
              "Other:",
              radioIsOtherSelected ? E(TextField, {size:"small", variant:"standard",
                value:radioOtherText,
                placeholder:"Please specify",
                disabled:props.readOnly,
                sx:{minWidth:200},
                onClick:function(e){ e.stopPropagation(); },
                onChange:function(e){ props.onChange(field.id, "Other: " + e.target.value); }}) : null)
          }) : null));
    case "checkbox":
      var checked = Array.isArray(value) ? value : [];
      var cbAllowOther = field.validation && field.validation.allow_other;
      var cbOtherEntry = null;
      var cbCheckedClean = checked.slice();
      if (cbAllowOther) {
        for (var ci2 = 0; ci2 < checked.length; ci2++) {
          if (checked[ci2] && checked[ci2].indexOf("Other: ") === 0) {
            cbOtherEntry = checked[ci2];
            break;
          }
        }
      }
      var cbOtherChecked = cbOtherEntry !== null;
      var cbOtherText = cbOtherChecked ? cbOtherEntry.substring(7) : "";
      return E(FieldWrapper, {field:field, error:error},
        E(FormGroup, null,
          (field.options||[]).map(function(o){
            return E(FormControlLabel,{key:o,label:o,control:E(Checkbox,{checked:checked.indexOf(o)>=0,
              disabled:props.readOnly,
              onChange:function(e){props.onChange(field.id, e.target.checked ? checked.concat([o]) : checked.filter(function(x){return x!==o;}));}})});}),
          cbAllowOther ? E(FormControlLabel, {key:"__other__",
            control:E(Checkbox, {checked:cbOtherChecked, disabled:props.readOnly,
              onChange:function(e){
                if (e.target.checked) {
                  props.onChange(field.id, checked.concat(["Other: "]));
                } else {
                  props.onChange(field.id, checked.filter(function(x){ return !x || x.indexOf("Other: ") !== 0; }));
                }
              }}),
            label:E(Box, {sx:{display:"flex", alignItems:"center", gap:1}},
              "Other:",
              cbOtherChecked ? E(TextField, {size:"small", variant:"standard",
                value:cbOtherText,
                placeholder:"Please specify",
                disabled:props.readOnly,
                sx:{minWidth:200},
                onClick:function(e){ e.stopPropagation(); },
                onChange:function(e){
                  var newChecked = checked.filter(function(x){ return !x || x.indexOf("Other: ") !== 0; });
                  newChecked.push("Other: " + e.target.value);
                  props.onChange(field.id, newChecked);
                }}) : null)
          }) : null));
    case "file_upload":
      return E(FileUploadField, {field:field, value:value, error:error, onFileChange:props.onFileChange, readOnly:props.readOnly});
    case "video":
      return E(VideoSelector, {field:field, value:value, onChange:props.onChange, error:error, readOnly:props.readOnly});
    case "terms_acceptance":
      return E(TermsField, {field:field, value:value, onChange:props.onChange, error:error, readOnly:props.readOnly});
    default:
      return E(FieldWrapper, {field:field, error:error},
        E(TextField, {fullWidth:true, size:"small", value:value,
          onChange:function(e){props.onChange(field.id,e.target.value);}, disabled:props.readOnly}));
  }
}

export default function FormRenderer(props) {
  if (!props.schema) return null;
  var E = React.createElement;
  var fields = (props.schema.fields||[]).slice().sort(function(a,b){return a.order-b.order;});
  var sections = (props.schema.sections||[]).slice().sort(function(a,b){return a.order-b.order;});
  var unsectioned = fields.filter(function(f){return !f.section_id && (!f.scholar_only||props.scholarOnly);});
  return E(Box, null,
    unsectioned.map(function(f){return E(Box,{key:f.id,sx:{mb:2.5}}, E(RenderField,{field:f,values:props.values||{},onChange:props.onChange||function(){},onFileChange:props.onFileChange,errors:props.errors,readOnly:props.readOnly,allFields:fields}));}),
    sections.map(function(sec){
      var sf = fields.filter(function(f){return f.section_id===sec.id && (!f.scholar_only||props.scholarOnly);});
      if (!sf.length) return null;
      return E(Paper,{key:sec.id,variant:"outlined",sx:{p:3,mb:3}},
        E(Typography,{variant:"h6",sx:{mb:0.5,color:"primary.main"}},sec.title),
        sec.description ? E(DescriptionText, {text:sec.description, sx:{mb:2}}) : null,
        sf.map(function(f){return E(Box,{key:f.id,sx:{mb:2.5}}, E(RenderField,{field:f,values:props.values||{},onChange:props.onChange||function(){},onFileChange:props.onFileChange,errors:props.errors,readOnly:props.readOnly,allFields:fields}));}));}));
}

export function MultiStepFormRenderer(props) {
  if (!props.schema) return null;
  var E = React.createElement;
  var fields = (props.schema.fields||[]).slice().sort(function(a,b){return a.order-b.order;});
  var sections = (props.schema.sections||[]).slice().sort(function(a,b){return a.order-b.order;});
  if (sections.length===0) {
    return E(Box, null, fields.filter(function(f){return !f.scholar_only||props.scholarOnly;}).map(function(f){
      return E(Box,{key:f.id,sx:{mb:2.5}}, E(RenderField,{field:f,values:props.values||{},onChange:props.onChange||function(){},onFileChange:props.onFileChange,errors:props.errors,readOnly:props.readOnly,allFields:fields}));}));
  }
  var section = sections[props.currentStep] || sections[0];
  var sectionDesc = section && section.description ? E(DescriptionText, {text:section.description, sx:{mb:2}}) : null;
  var sf = fields.filter(function(f){return f.section_id===section.id && (!f.scholar_only||props.scholarOnly);});
  return E(Box, null, sectionDesc,
    sf.map(function(f){ return E(Box,{key:f.id,sx:{mb:2.5}}, E(RenderField,{field:f,values:props.values||{},onChange:props.onChange||function(){},onFileChange:props.onFileChange,errors:props.errors,readOnly:props.readOnly,allFields:fields}));}));
}

export var SinglePageFormRenderer = FormRenderer;
